/**
 * trace-model.ts — the pure shaping behind the Trace view. It folds the workspace's
 * Assistant runs and the raw event log into one ordered list of rows, extracts the
 * subjects (entity URNs) each row touches so a person can follow a thread across them,
 * and decides what matches the active filter. Kept free of React and the workspace
 * resolver so it can be unit-tested; the view resolves URNs to names at render time.
 */
import type { AgentRun, DomainEvent, TurnEdit, Workspace } from "../../store/events.ts";
import { isUrn } from "../../domain/urn.ts";
import type { Locale } from "./i18n.tsx";

export type TraceActor = "human" | "agent" | "system";

export interface TraceRow {
  /** Stable React key / identity. */
  readonly key: string;
  readonly kind: "run" | "event";
  readonly actor: TraceActor;
  readonly ts: string;
  /** Primary human-facing line (a run's prompt, or an event's humanized type). */
  readonly title: string;
  /** The raw machine type, shown faintly in monospace. */
  readonly typeLabel: string;
  /** Entity URNs this row references — the threads it can be followed along. */
  readonly subjects: readonly string[];
  /** Application routes this row touched (agent navigations). */
  readonly routes: readonly string[];
  readonly run?: AgentRun;
  readonly event?: DomainEvent;
  /** Lowercased text the search box matches against. */
  readonly haystack: string;
}

/** Recursively gather unique entity URNs from any value. */
export function collectUrns(value: unknown, acc: Set<string> = new Set()): string[] {
  if (typeof value === "string") {
    if (isUrn(value)) acc.add(value);
  } else if (Array.isArray(value)) {
    for (const v of value) collectUrns(v, acc);
  } else if (value && typeof value === "object") {
    for (const v of Object.values(value)) collectUrns(v, acc);
  }
  return [...acc];
}

/** "asset.approved" → "Asset approved"; "agent.run.finished" → "Agent run finished". */
export function humanizeType(type: string): string {
  const words = type.replace(/\./g, " ").split(/\s+/).filter(Boolean);
  return words
    .map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/** Routes an agent run touched: its origin route plus every navigate step's path. */
function runRoutes(run: AgentRun): string[] {
  const routes = new Set<string>();
  if (run.route) routes.add(run.route);
  for (const s of run.steps) if (s.kind === "navigate" && s.summary) routes.add(s.summary);
  return [...routes];
}

/** Build the unified, newest-first list of trace rows from runs + the event log. */
export function buildTraceRows(ws: Workspace, events: readonly DomainEvent[]): TraceRow[] {
  const rows: TraceRow[] = [];

  for (const run of Object.values(ws.agentRun)) {
    const subjects = collectUrns(run.steps).concat(collectUrns(run.edits));
    const haystack = [run.prompt, run.text, run.reasoning ?? "", run.model, run.provider, ...run.steps.map((s) => `${s.name ?? ""} ${s.summary ?? ""}`)]
      .join(" ")
      .toLowerCase();
    rows.push({
      key: `run:${run.runId}`,
      kind: "run",
      actor: "agent",
      ts: run.createdAt,
      title: run.prompt || "(assistant run)",
      typeLabel: "assistant run",
      subjects: [...new Set(subjects)],
      routes: runRoutes(run),
      run,
      haystack,
    });
  }

  for (const e of events) {
    const subjects = collectUrns(e.payload);
    const path = (e.payload as { path?: unknown }).path;
    const routes = typeof path === "string" ? [path] : [];
    const haystack = `${e.type} ${e.actor.type} ${e.actor.id} ${JSON.stringify(e.payload)}`.toLowerCase();
    rows.push({
      key: `evt:${e.eventId}`,
      kind: "event",
      actor: e.actor.type,
      ts: e.ts,
      title: humanizeType(e.type),
      typeLabel: e.type,
      subjects,
      routes,
      event: e,
      haystack,
    });
  }

  // Newest first; ties broken by event seq so a single instant stays ordered.
  return rows.sort((a, b) => {
    const t = b.ts.localeCompare(a.ts);
    if (t !== 0) return t;
    return (b.event?.seq ?? 0) - (a.event?.seq ?? 0);
  });
}

export interface TraceFilter {
  readonly text: string;
  readonly actor: TraceActor | "all";
  /** When set, only rows whose subjects include this URN — a followed thread. */
  readonly thread: string | null;
}

export const EMPTY_FILTER: TraceFilter = { text: "", actor: "all", thread: null };

/** Token-AND text match plus actor and thread predicates (mirrors the palette's search). */
export function matchesFilter(row: TraceRow, f: TraceFilter): boolean {
  if (f.thread && !row.subjects.includes(f.thread)) return false;
  if (f.actor !== "all") {
    // A run carries both a human prompt and an AI reply, so it satisfies either lens.
    const ok = row.kind === "run" ? f.actor === "agent" || f.actor === "human" : row.actor === f.actor;
    if (!ok) return false;
  }
  const tokens = f.text.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return tokens.every((tok) => row.haystack.includes(tok));
}

/**
 * Compose the text a replay sends to the model, honoring the edit's disclosure mode.
 * This is the lever that makes the chosen transparency real: the same revised value
 * reaches the model wrapped in more or less of its own history.
 *
 *   • prompt edit      — simply re-ask the revised question (no AI history to disclose).
 *   • response · full       — original + final (+ intent): teach the model the correction.
 *   • response · final-only — the final value standing alone, as if it always had.
 *   • response · flagged    — the final value, marked human-edited.
 *
 * It only ever builds a user turn; replay runs through the same governed console, so
 * nothing here crosses a boundary.
 */
export function composeReplay(run: AgentRun, edit: TurnEdit, locale: Locale = "en"): string {
  const de = locale === "de";
  if (edit.turn === "prompt") return edit.revised;

  const ctx = run.prompt
    ? de
      ? `Zur ursprünglichen Frage: «${run.prompt}»\n\n`
      : `Regarding the original question: «${run.prompt}»\n\n`
    : "";
  const intent = edit.intent ? (de ? `\nAbsicht der Änderung: ${edit.intent}` : `\nIntent of the change: ${edit.intent}`) : "";

  switch (edit.disclosure) {
    case "full":
      return de
        ? `${ctx}Du hattest geantwortet: «${edit.original}». Ich habe deine Antwort überarbeitet zu: «${edit.revised}».${intent}\nFahre auf Basis der überarbeiteten Fassung fort.`
        : `${ctx}You had answered: «${edit.original}». I have revised your answer to: «${edit.revised}».${intent}\nContinue from the revised version.`;
    case "flagged":
      return de
        ? `${ctx}Hier ist die Antwort (vom Menschen bearbeitet): «${edit.revised}». Fahre auf dieser Grundlage fort.`
        : `${ctx}Here is the answer (human-edited): «${edit.revised}». Continue from it.`;
    case "final-only":
    default:
      return de
        ? `${ctx}Nimm dies als die etablierte Antwort und fahre fort: «${edit.revised}»`
        : `${ctx}Treat this as the established answer and continue: «${edit.revised}»`;
  }
}

/** The most recent edit to a given turn of a run, if any (edits are append-only). */
export function latestEdit(run: AgentRun, turn: TurnEdit["turn"]): TurnEdit | undefined {
  for (let i = run.edits.length - 1; i >= 0; i--) if (run.edits[i]!.turn === turn) return run.edits[i];
  return undefined;
}
